import { useState } from "react";
import { Crown, X, Send, Check, Sparkles } from "lucide-react";
import api, { formatErr } from "@/lib/api";

/**
 * Royal Request modal — unlocked via the secret JAVI G tap-tap-tap-hold combo.
 * Golden / regal styling (intentionally different from the cyan player) to
 * make it feel like a hidden treasure for the true fans.
 */
export default function KingRequestModal({ open, onClose }) {
  const [name, setName] = useState("");
  const [song, setSong] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  if (!open) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await api.post("/king-requests", {
        name: name.trim(),
        song: song.trim(),
        message: message.trim(),
      });
      setSuccess(true);
      setName(""); setSong(""); setMessage("");
      setTimeout(() => { setSuccess(false); onClose?.(); }, 2800);
    } catch (err) {
      setError(formatErr(err.response?.data?.detail) || "Couldn't send your royal request");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center px-4 bg-black/80 backdrop-blur-md"
      onClick={onClose}
      data-testid="king-modal"
    >
      <div
        className="relative w-full max-w-[420px] p-7 rounded-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "linear-gradient(180deg, #1a1108 0%, #0a0606 100%)",
          border: "1px solid rgba(212, 175, 55, 0.4)",
          boxShadow: "0 30px 80px rgba(0,0,0,0.7), 0 0 60px rgba(212, 175, 55, 0.15), inset 0 1px 0 rgba(255, 215, 0, 0.15)",
        }}
      >
        {/* gold gradient top accent */}
        <div
          className="absolute top-0 left-0 right-0 h-[3px]"
          style={{ background: "linear-gradient(90deg, transparent, #d4af37 30%, #ffd700 50%, #d4af37 70%, transparent)" }}
        />
        {/* sparkle background */}
        <div className="absolute -top-10 -right-10 opacity-10 pointer-events-none">
          <Crown size={180} className="text-[#ffd700]" strokeWidth={1} />
        </div>

        <button
          type="button"
          onClick={onClose}
          data-testid="king-close-button"
          className="absolute top-3 right-3 text-[#d4af37]/60 hover:text-[#ffd700] transition-colors z-10"
          aria-label="Close"
        >
          <X size={18} />
        </button>

        <div className="relative">
          <div className="flex items-center gap-2 mb-2">
            <Crown size={18} className="text-[#ffd700]" />
            <span
              className="text-[10px] font-mono uppercase tracking-[0.3em]"
              style={{ color: "#d4af37" }}
            >
              Hidden Chamber Unlocked
            </span>
            <Sparkles size={12} className="text-[#ffd700] animate-pulse" />
          </div>
          <h2
            className="font-display text-2xl font-black mb-1"
            style={{ background: "linear-gradient(180deg, #ffd700 0%, #d4af37 50%, #b8860b 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", filter: "drop-shadow(0 2px 4px rgba(212, 175, 55, 0.4))" }}
            data-testid="king-title"
          >
            Royal Request
          </h2>
          <p className="text-[#d4af37]/80 text-sm mb-5 leading-relaxed">
            You found the hidden chamber, fellow Mixxflow King.{" "}
            Send <span className="text-[#ffd700] font-semibold">JAVI G</span> a VIP track request — it lands at the top of the mixer's stack.
          </p>

          {success ? (
            <div className="py-10 text-center" data-testid="king-success">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[rgba(212,175,55,0.15)] border border-[#d4af37] mb-3">
                <Check size={28} className="text-[#ffd700]" />
              </div>
              <p className="text-[#ffd700] font-display text-lg font-bold mb-1">Request sealed 👑</p>
              <p className="text-[#d4af37]/80 text-sm">Listen for it in the rotation.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <label className="text-[10px] font-mono uppercase tracking-widest text-[#d4af37]/80 block mb-1">
                  Your court name
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={60}
                  placeholder="King Alex, Queen Mariana…"
                  data-testid="king-name-input"
                  className="w-full bg-[#0d0805] border border-[rgba(212,175,55,0.3)] focus:border-[#ffd700] focus:shadow-[0_0_0_3px_rgba(212,175,55,0.15)] text-white placeholder-[#6b5a2c] rounded-md px-3.5 py-2.5 text-sm outline-none transition-all"
                />
              </div>
              <div>
                <label className="text-[10px] font-mono uppercase tracking-widest text-[#d4af37]/80 block mb-1">
                  Track requested *
                </label>
                <input
                  required
                  type="text"
                  value={song}
                  onChange={(e) => setSong(e.target.value)}
                  maxLength={160}
                  placeholder="Artist – Track (remix?)"
                  data-testid="king-song-input"
                  className="w-full bg-[#0d0805] border border-[rgba(212,175,55,0.3)] focus:border-[#ffd700] focus:shadow-[0_0_0_3px_rgba(212,175,55,0.15)] text-white placeholder-[#6b5a2c] rounded-md px-3.5 py-2.5 text-sm outline-none transition-all"
                />
              </div>
              <div>
                <label className="text-[10px] font-mono uppercase tracking-widest text-[#d4af37]/80 block mb-1">
                  A note for the DJ <span className="text-[#6b5a2c]">(optional)</span>
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  maxLength={400}
                  rows={3}
                  placeholder="Why this track? Dedication? Time it should play?"
                  data-testid="king-message-input"
                  className="w-full bg-[#0d0805] border border-[rgba(212,175,55,0.3)] focus:border-[#ffd700] focus:shadow-[0_0_0_3px_rgba(212,175,55,0.15)] text-white placeholder-[#6b5a2c] rounded-md px-3.5 py-2.5 text-sm outline-none transition-all resize-none"
                />
              </div>

              {error && (
                <p className="text-[#ff5566] text-xs font-mono text-center" data-testid="king-error">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={submitting}
                data-testid="king-submit-button"
                className="w-full py-3 rounded-md font-display font-bold text-sm tracking-widest uppercase flex items-center justify-center gap-2 transition-all"
                style={{
                  background: "linear-gradient(135deg, #ffd700 0%, #d4af37 50%, #b8860b 100%)",
                  color: "#1a1108",
                  boxShadow: "0 8px 24px rgba(212, 175, 55, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.3)",
                  opacity: submitting ? 0.6 : 1,
                  cursor: submitting ? "wait" : "pointer",
                }}
              >
                <Send size={14} /> {submitting ? "Sending..." : "Submit royal request"}
              </button>
            </form>
          )}

          <p className="text-center text-[9px] font-mono uppercase tracking-[0.3em] text-[#6b5a2c] mt-5">
            VIP · Limited to 3 requests / hour
          </p>
        </div>
      </div>
    </div>
  );
}
